const OBSERVER_ROOT_MARGIN = '0px 0px 320px 0px';

export default function infiniteFeed({ main, elm, state }) {
	const itemsSelector = elm.dataset.itemsSelector || '[data-feed-items]';
	const itemSelector = elm.dataset.itemSelector || '.article';
	const loaderSelector = elm.dataset.loaderSelector || '[data-feed-loader]';
	const paginationSelector = elm.dataset.paginationSelector || '[data-feed-pagination]';
	const feedState = {
		nextUrl: elm.dataset.nextUrl || '',
		isLoading: false,
		isEnhanced: false
	};
	let itemsContainer = null;
	let loader = null;
	let pagination = null;
	let itemKeys = new Set();
	let observer = null;

	main(() => {
		itemsContainer = elm.querySelector(itemsSelector);
		loader = elm.querySelector(loaderSelector);
		pagination = elm.querySelector(paginationSelector);

		if (!itemsContainer || !loader || !pagination) {
			return;
		}

		itemKeys = new Set(collectItemKeys(itemsContainer, itemSelector));
		observer = createObserver();

		if (!observer) {
			loader.hidden = true;
			return;
		}

		feedState.nextUrl = feedState.nextUrl || getInitialState().nextUrl || extractNextUrl(pagination);
		feedState.isEnhanced = true;
		state?.save?.(feedState);

		elm.classList.add('is-enhanced');
		syncLoader();

		if (feedState.nextUrl) {
			observer.observe(loader);
		}
	});

	function createObserver() {
		if (!('IntersectionObserver' in window)) {
			return null;
		}

		return new IntersectionObserver(handleIntersect, {
			rootMargin: OBSERVER_ROOT_MARGIN,
			threshold: 0
		});
	}

	function handleIntersect(entries) {
		const entry = entries[0];

		if (!entry || !entry.isIntersecting || feedState.isLoading || !feedState.nextUrl) {
			return;
		}

		void loadNextPage();
	}

	async function loadNextPage() {
		const nextUrl = feedState.nextUrl;

		if (feedState.isLoading || !nextUrl) {
			return;
		}

		feedState.isLoading = true;
		state?.save?.(feedState);
		syncLoader();

		try {
			const response = await fetch(nextUrl, {
				headers: {
					'X-Requested-With': 'XMLHttpRequest'
				}
			});

			if (!response.ok) {
				throw new Error(`Infinite feed request failed: ${response.status}`);
			}

			const html = await response.text();
			const documentNode = new DOMParser().parseFromString(html, 'text/html');
			const incomingRoot = documentNode.querySelector('infinite-feed, blog-load-more');
			const incomingItemsContainer = incomingRoot?.querySelector(itemsSelector);
			const incomingPagination = incomingRoot?.querySelector(paginationSelector);

			if (!incomingRoot || !incomingItemsContainer || !incomingPagination) {
				throw new Error('Infinite feed markup is missing required targets');
			}

			const incomingItems = [...incomingItemsContainer.querySelectorAll(itemSelector)];
			const newItems = incomingItems.filter((item) => {
				const key = getItemKey(item);

				if (key && itemKeys.has(key)) {
					return false;
				}

				if (key) {
					itemKeys.add(key);
				}

				item.classList.add('is-feed-incoming');

				return true;
			});

			if (newItems.length) {
				itemsContainer.append(...newItems);
				revealItems(newItems);
			}

			pagination.innerHTML = incomingPagination.innerHTML;

			feedState.nextUrl = incomingRoot.dataset.nextUrl || extractNextUrl(incomingPagination);
			state?.save?.(feedState);
		} catch (error) {
			console.error(error);
		} finally {
			feedState.isLoading = false;
			state?.save?.(feedState);
			syncLoader();
		}
	}

	function syncLoader() {
		if (!loader || !pagination) {
			return;
		}

		const hasMore = Boolean(feedState.nextUrl);

		pagination.hidden = true;
		loader.hidden = !hasMore && !feedState.isLoading;
		loader.classList.toggle('is-loading', feedState.isLoading);

		if (observer && !hasMore && !feedState.isLoading) {
			observer.disconnect();
			observer = null;
		}

		if (!observer && hasMore) {
			observer = createObserver();

			if (observer) {
				observer.observe(loader);
			}
		}
	}

	function revealItems(items) {
		requestAnimationFrame(() => {
			items.forEach((item, index) => {
				item.style.setProperty('--feed-enter-delay', `${index * 80}ms`);
				item.classList.add('is-feed-revealed');

				window.setTimeout(() => {
					item.classList.remove('is-feed-incoming', 'is-feed-revealed');
					item.style.removeProperty('--feed-enter-delay');
				}, 700 + index * 80);
			});
		});
	}

	function getInitialState() {
		try {
			if (state?.get) {
				return state.get();
			}
		} catch (error) {
			console.error(error);
		}

		return {};
	}
}

function extractNextUrl(pagination) {
	if (!pagination) {
		return '';
	}

	const nextLink = [...pagination.querySelectorAll('.page-link')]
		.find((link) => link.textContent.trim() === '>');

	return nextLink?.getAttribute('href') || '';
}

function collectItemKeys(container, selector) {
	return [...container.querySelectorAll(selector)]
		.map(getItemKey)
		.filter(Boolean);
}

function getItemKey(item) {
	if (!item) {
		return '';
	}

	const directHref = item.getAttribute('href');

	if (directHref) {
		return directHref;
	}

	const nestedHref = item.querySelector('[href]')?.getAttribute('href');

	if (nestedHref) {
		return nestedHref;
	}

	return item.textContent?.trim() || '';
}

export const model = ({ elm }) => ({
	nextUrl: elm.dataset.nextUrl || '',
	isLoading: false,
	isEnhanced: false
});

export const view = (state) => state;
